import { CourseCard } from '@/components/ui/custom/coursePath/card/coursePathCard/CoursePathCard';
import useStudentContext from '@/data/hook/useStudentContext';
import { EnrollmentStatusEnum } from '@/models/Enrollment';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import { getBannerImg } from '@/utils/coursePath';
import { ROUTES } from '@/utils/routes';
import { ClockIcon } from '@heroicons/react/24/outline';

type CourseProgressCardProps = {
  courseProgress: SimplifiedCourseProgress;
  onMutate?: (courseProgress: SimplifiedCourseProgress) => void;
};

export default function SyncCourseProgressCard({
  courseProgress,
}: CourseProgressCardProps) {
  const { coursePath, progress, enrollmentStatus } = courseProgress;

  const { dashboardStatsCollection, selectProgress } = useStudentContext();

  const thisProgress = dashboardStatsCollection.find(
    progress => progress.id === courseProgress.id,
  );

  const courseBannerImg = getBannerImg(coursePath);
  const courseName = coursePath.course.name;
  const totalLessons = thisProgress?.progresses.lessons.total || 0;
  const completedLessons = thisProgress?.progresses.lessons.completed || 0;
  const remainingLessons = totalLessons - completedLessons;
  const getCourseCardLink = () => {
    if (courseProgress.enrollmentStatus === EnrollmentStatusEnum.CANCELED) {
      return ROUTES.COURSES.INFO(coursePath.slug);
    }
    return ROUTES.COURSES.INFO(coursePath.slug, courseProgress.klassId);
  };

  return (
    <CourseCard
      to={getCourseCardLink()}
      onClick={() => selectProgress(courseProgress.id)}
    >
      <CourseCard.Header>
        <CourseCard.Image src={courseBannerImg} />
      </CourseCard.Header>
      <CourseCard.Body
        className={{
          base: 'flex flex-row gap-2 w-full justify-between items-center',
        }}
      >
        <div className="flex flex-row gap-2 w-full truncate">
          <CourseCard.Title text={courseName} />
          <CourseCard.ProgressIcon
            progress={progress}
            enrollmentStatus={enrollmentStatus}
            icon={<ClockIcon className="w-6 h-6 shrink-0 text-primary" />}
          />
        </div>
        <CourseCard.Formats formats={coursePath?.course?.formats} />
      </CourseCard.Body>

      <CourseCard.Footer className={{ base: 'flex flex-col gap-2 w-full' }}>
        <CourseCard.Progress value={progress} color="primary" />
        <CourseCard.ProgressStats
          totalLessons={totalLessons}
          remainingLessons={remainingLessons}
          courseProgress={courseProgress}
          className={{ section: 'text-primary' }}
        />
      </CourseCard.Footer>
    </CourseCard>
  );
}
